/**
 * Hero Lumeska — progressive enhancement layer.
 * Works with zero JS (section renders fully via CSS/HTML).
 * GSAP + ScrollTrigger are fetched from CDN only when motion is allowed,
 * and only after the browser is idle, so they never block first paint.
 */
(function () {
  const GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const SCROLLTRIGGER_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const saveDataEnabled = () => 'connection' in navigator && navigator.connection.saveData;

  let gsapLoadPromise = null;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function loadGsap() {
    if (gsapLoadPromise) return gsapLoadPromise;
    gsapLoadPromise = loadScript(GSAP_URL)
      .then(() => loadScript(SCROLLTRIGGER_URL))
      .then(() => {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
        }
        return window.gsap;
      })
      .catch(() => null);
    return gsapLoadPromise;
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1500 });
    } else {
      window.setTimeout(callback, 300);
    }
  }

  class HeroLumeska {
    constructor(root) {
      this.root = root;
      this.video = root.querySelector('.hero-lumeska__video');
      this.visual = root.querySelector('.hero-lumeska__visual');
      this.motionToggle = root.querySelector('.hero-lumeska__motion-toggle');
      this.reducedMotion = prefersReducedMotion();
      this.motionPaused = this.reducedMotion;
      this.enableParallax = root.dataset.parallax === 'true';
      this.enableParticles = root.dataset.particles === 'true';
      this.videoOnMobile = root.dataset.videoMobile === 'true';

      if (this.motionPaused) root.classList.add('is-motion-paused');

      this.bindMotionToggle();
      this.setupVideo();
      this.setupEnhancements();
      this.setupLedInteraction();
    }

    bindMotionToggle() {
      if (!this.motionToggle) return;
      this.motionToggle.setAttribute('aria-pressed', String(!this.motionPaused));
      this.motionToggle.addEventListener('click', () => {
        this.motionPaused = !this.motionPaused;
        this.root.classList.toggle('is-motion-paused', this.motionPaused);
        this.motionToggle.setAttribute('aria-pressed', String(!this.motionPaused));

        if (this.video) {
          if (this.motionPaused) this.video.pause();
          else this.video.play().catch(() => {});
        }
      });
    }

    setupLedInteraction() {
      // Real <button> tabs everywhere, but the visible "active" swatch
      // ring and the benefit panel only ever show under 749px (CSS-gated)
      // — this is harmless on desktop, just invisible there.
      const buttons = Array.from(this.root.querySelectorAll('.hero-lumeska__led-item'));
      const panels = Array.from(this.root.querySelectorAll('.hero-lumeska__led-panel'));
      if (!buttons.length || !panels.length) return;

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const targetId = button.dataset.target;
          if (!targetId || button.getAttribute('aria-selected') === 'true') return;

          buttons.forEach((item) => item.setAttribute('aria-selected', String(item === button)));
          panels.forEach((panel) => {
            const active = panel.id === targetId;
            panel.dataset.active = String(active);
            panel.setAttribute('aria-hidden', String(!active));
          });
        });
      });
    }

    setupVideo() {
      if (!this.video) return;

      // Fuerza muted también por propiedad JS, no solo por atributo HTML:
      // en iOS Safari el autoplay silencioso solo es fiable si el atributo
      // HTML `autoplay` está presente desde el primer parseo del documento
      // (no basta con llamar a .play() más tarde desde JS) y si además la
      // propiedad `muted` coincide antes de cualquier intento de reproducción.
      this.video.muted = true;
      this.video.defaultMuted = true;

      const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
      const shouldSkip = this.reducedMotion || saveDataEnabled() || (isMobileViewport && !this.videoOnMobile);

      if (shouldSkip) {
        // El vídeo lleva el atributo HTML `autoplay` (necesario para que
        // Safari/iOS lo reproduzca de forma fiable), así que si no debe
        // reproducirse en este caso hay que detenerlo explícitamente en
        // lugar de limitarse a no llamar a play().
        this.video.pause();
        return;
      }

      this.video.addEventListener('playing', () => {
        this.root.classList.add('is-video-playing');
      });

      if (this.video.paused) {
        // El autoplay nativo no arrancó todavía (navegador aún cargando,
        // Modo de bajo consumo en iOS, etc.) — reintenta en cuanto el
        // navegador esté libre.
        whenIdle(() => this.playVideo());
      } else {
        this.root.classList.add('is-video-playing');
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (this.video.paused && !this.motionPaused) this.playVideo();
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(this.root);
    }

    playVideo() {
      if (!this.video || this.motionPaused) return;
      const playPromise = this.video.play();
      if (!playPromise || typeof playPromise.then !== 'function') return;
      playPromise
        .then(() => this.root.classList.add('is-video-playing'))
        .catch(() => {
          // Autoplay bloqueado por el navegador (p. ej. iOS con Modo de bajo
          // consumo activo): deja el póster visible y reintenta en el
          // primer gesto real del usuario en la página.
          this.retryVideoOnInteraction();
        });
    }

    retryVideoOnInteraction() {
      if (this.interactionRetryBound || !this.video) return;
      this.interactionRetryBound = true;

      const events = ['touchstart', 'click', 'scroll'];
      const retry = () => {
        events.forEach((ev) => document.removeEventListener(ev, retry));
        if (this.motionPaused) return;
        this.video
          .play()
          .then(() => this.root.classList.add('is-video-playing'))
          .catch(() => {});
      };
      events.forEach((ev) => document.addEventListener(ev, retry, { passive: true }));
    }

    setupEnhancements() {
      if (this.reducedMotion) return;
      if (!this.enableParallax && !this.visual) return;

      whenIdle(() => {
        loadGsap().then((gsap) => {
          if (!gsap) return;
          this.animateEntrance(gsap);
          if (this.enableParallax) this.animateParallax(gsap);
          this.animateTilt(gsap);
        });
      });
    }

    animateEntrance(gsap) {
      const eyebrow = this.root.querySelector('.hero-lumeska__eyebrow');
      const heading = this.root.querySelector('.hero-lumeska__heading');
      const text = this.root.querySelector('.hero-lumeska__text');
      const buttons = this.root.querySelector('.hero-lumeska__buttons');
      const targets = [eyebrow, heading, text, buttons].filter(Boolean);

      // On mobile with a video hero, the content now sits below a
      // full-height video stage — reached only by scrolling, not visible
      // on load. Firing the fade-up immediately (as desktop does, since
      // its content is visible alongside the video from the start) would
      // mean the animation plays off-screen before anyone scrolls to it.
      // Gating it on scroll position instead makes it read as a
      // deliberate reveal timed to arrival, not a missed intro.
      const isMobileVideoStage =
        window.matchMedia('(max-width: 749px)').matches && this.root.classList.contains('hero-lumeska--has-video');

      if (targets.length) {
        if (isMobileVideoStage && window.ScrollTrigger) {
          // Luxury reveal, not a "pop": short (600ms) fade + modest lift +
          // a whisper of blur that resolves as it settles — the kind of
          // restrained motion Apple/Aesop product pages use, not a
          // scroll-jacking flourish. Kept deliberately separate from the
          // desktop timing below so desktop is untouched.
          gsap.from(targets, {
            opacity: 0,
            y: 24,
            filter: 'blur(6px)',
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.08,
            scrollTrigger: {
              trigger: this.root.querySelector('.hero-lumeska__content') || this.root,
              start: 'top 88%',
            },
          });
        } else {
          gsap.from(targets, {
            opacity: 0,
            y: 28,
            duration: 0.9,
            ease: 'power3.out',
            stagger: 0.12,
          });
        }
      }

      if (this.visual) {
        if (isMobileVideoStage && window.ScrollTrigger) {
          gsap.from(this.visual, {
            opacity: 0,
            scale: 0.97,
            y: 24,
            filter: 'blur(6px)',
            duration: 0.65,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: this.visual,
              start: 'top 90%',
            },
          });
        } else {
          gsap.from(this.visual, {
            opacity: 0,
            scale: 0.92,
            y: 24,
            duration: 1.1,
            ease: 'power3.out',
            delay: 0.15,
          });
        }
      }
    }

    animateParallax(gsap) {
      if (!window.ScrollTrigger) return;

      if (this.visual) {
        // Fixed pixel travel (not yPercent) so the card's parallax stays
        // capped at ~18px regardless of viewport height — "máximo 20px,
        // muy elegante, sin marear".
        gsap.to(this.visual, {
          y: -18,
          ease: 'none',
          scrollTrigger: {
            trigger: this.root,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      const media = this.root.querySelector('.hero-lumeska__media');
      if (media) {
        // start: 'top top' keeps progress at exactly 0 while the hero sits
        // at its natural load position (scrollY 0, hero top at viewport
        // top) — 'top bottom' was computing a non-zero progress before any
        // scrolling happened, shifting the image down and exposing the raw
        // section background above it.
        const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
        const isMobileVideoStage = isMobileViewport && this.root.classList.contains('hero-lumeska--has-video');
        gsap.to(media, {
          yPercent: isMobileViewport ? 4 : 10,
          ease: 'none',
          scrollTrigger: {
            trigger: this.root,
            start: 'top top',
            // On mobile the video "stage" is pinned to a fixed ~100svh at
            // the top, with a lot more section below it now (stacked
            // content). Ending at 'bottom top' would spread the same
            // yPercent shift across that entire extra scroll distance,
            // diluting it to almost nothing by the time the video is
            // actually leaving view. Ending exactly one viewport height
            // in keeps the parallax scoped to just the stage itself.
            end: isMobileVideoStage ? '+=100%' : 'bottom top',
            scrub: true,
          },
        });
      }
    }

    animateTilt(gsap) {
      if (!this.visual) return;
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!canHover) return;

      const rotateX = gsap.quickTo(this.visual, 'rotationX', { duration: 0.6, ease: 'power3' });
      const rotateY = gsap.quickTo(this.visual, 'rotationY', { duration: 0.6, ease: 'power3' });

      this.root.addEventListener('pointermove', (event) => {
        const bounds = this.root.getBoundingClientRect();
        const relX = (event.clientX - bounds.left) / bounds.width - 0.5;
        const relY = (event.clientY - bounds.top) / bounds.height - 0.5;
        rotateY(relX * 10);
        rotateX(relY * -10);
      });

      this.root.addEventListener('pointerleave', () => {
        rotateX(0);
        rotateY(0);
      });
    }
  }

  function initAll(root = document) {
    root.querySelectorAll('.hero-lumeska:not([data-hero-initialized])').forEach((el) => {
      el.setAttribute('data-hero-initialized', 'true');
      new HeroLumeska(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
